import React, { useMemo, useState } from 'react';
import {
  useGetList,
  useGetIdentity,
  useStore,
  useNotify,
  RecordContextProvider,
  ReferenceField,
  TextField,
} from 'react-admin';
import { useFuneralDrawer } from './FuneralDrawerContext';
import { awaitingPaperworkConfirmationEmail } from './caseClosure';
import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { resendPaperworkLink } from './workItemsApi';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { CremationStatus } from '../types';
import { isPickupMethod, pickupNeedsDate, PICKUP_TBD_LABEL } from '../deliveries/pickupDateTbd';
import { CustomBooleanField } from '../components/CustomBooleanField';
import { DecStatusField } from '../components/DecStatusField';
import { BenefitsListCell } from './BenefitsListCell';
import { BenefitsStatusProvider } from './BenefitsStatusField';
import { WorkItemComposer } from './WorkItemComposer';
import { SiedStatusCell } from './SiedStatusCell';
import { isPreplanOnly } from './preneedMode';
import { useQuery } from 'react-query';
import { supabase } from '../lib/supabaseClient';
import { preneedStageBadge } from './preneedTimeline';

/**
 * PoC: the funerals status group, re-skinned to shadcn/Tailwind (Cleo `@theme`)
 * on top of the SAME React-Admin data layer as the MUI `StatusGroup`.
 *
 * Design deltas from the current board (see the enriched mock):
 *  - Date-of-Death and the standing Contracts column are dropped; that space
 *    becomes a "Suggested next action" column.
 *  - Contract state shows only as an EXCEPTION here (unsigned / missing); the
 *    Passed + full-contract chips live in the Arrangement group only.
 *  - Owner avatar moves into the first column, next to the region.
 *
 * Functionality is preserved by REUSING the existing interactive editors
 * (`DecStatusField`, `BenefitsStatusFieldList`, `CustomBooleanField`) — their
 * shadcn port is a follow-up. The suggested-action content is stubbed until the
 * `case_objective` work-item endpoint lands (the agent's morning pass).
 */

// 5 columns: id · name · status · suggested action · state cells. The
// Status column shows case age (days in the current section) everywhere, plus
// the Passed / contract chips in Arrangement. The first state cell is SIED, the
// SP-3 form's process step in Québec's SIED portal (T-100; see ./siedStatus).
// Column templates are applied as inline styles (not Tailwind arbitrary
// grid-cols) so the layout never depends on the JIT generating a bracket class.
const GRID = 'grid items-center gap-3';
const GRID_COLS =
  '56px minmax(140px,1fr) minmax(110px,auto) minmax(0,1.5fr) 256px';

// SIED / Registry / DEC / Benefits / Attestation sub-grid. Benefits gets the
// slack (1fr) because it now shows text + aging; the others stay icon-width.
const STATUS_SUBGRID = 'grid items-center';
const STATUS_COLS = '32px 32px 32px 1fr 32px';

// Rows shown per group before the "Show N older" fold kicks in. Arrangement
// stays tighter — it's the intake funnel and older un-actioned rows are noise.
const FOLD_COUNT = 20;
const ARRANGEMENT_FOLD_COUNT = 6;

// Closure lives in ./caseClosure so the status-cell editors can share it
// without importing this module (which imports them).
export { isClosed } from './caseClosure';

type ContractSummary = {
  id: number;
  is_preneed: boolean;
  // `null` when the contract row exists but nothing has happened to it yet.
  // The view used to call that 'created'; it no longer labels a state it can't
  // observe, so the absence of a status IS the "not sent yet" signal.
  status: 'paid' | 'signed' | 'sent' | null;
};

type Suggestion = {
  tag: string;
  text: string;
  variant: 'destructive' | 'warning' | 'info' | 'success';
};

// Outside Arrangement we surface the contract only when something is wrong.
//
// Both branches read the funeral's send/sign timestamps, NOT whether a contract
// row exists. A row existing says nothing about whether the family ever saw it:
// keying off existence claimed "Contract sent · unsigned" on every case whose
// contract was created and never sent, which is simply false. The destructive
// 'No contract' alarm is preserved and now means what it always meant to say —
// no contract has been *sent* on this case, go look at it.
function contractException(
  record: any,
): { label: string; variant: 'destructive' | 'warning' } | null {
  if (record?.contracts_signed_at) return null;
  // Pre-plan only (T-172): no contract is expected, so none is missing.
  if (isPreplanOnly(record)) return null;
  if (record?.contracts_sent_at)
    return { label: 'Contract sent · unsigned', variant: 'warning' };
  return { label: 'No contract', variant: 'destructive' };
}

// Arrangement cases are pre-passing information-collection — a missing or
// unsigned contract is normal there, NOT a problem. So contract state is shown
// as neutral info (never a warning), and there's no chip at all when there's no
// contract yet — and none either when one exists but has never been sent, which
// is the same "nothing has happened yet" for the family as having no row at all.
function contractStageBadge(
  record: any,
  preneedContract?: { trustage_status?: string | null; trustage_sent_at?: string | null } | null,
): { label: string; variant: 'success' | 'secondary' | 'warning'; since?: string | null } | null {
  // Pre-plan only (T-172): grey "Preplan" instead of Sent, so nobody follows
  // up for signing or payment. A signed contract ends pre-plan only, so a
  // signed case still shows Signed.
  if (isPreplanOnly(record)) return { label: 'Preplan', variant: 'secondary' };
  // Pre-need TruStage flow (T-162): one word once TruStage has it.
  const preneed = preneedStageBadge(record, preneedContract);
  if (preneed) return preneed;
  if (record?.contracts_signed_at)
    return { label: 'Signed', variant: 'success', since: record.contracts_signed_at };
  if (record?.contracts_sent_at)
    return { label: 'Sent', variant: 'secondary', since: record.contracts_sent_at };
  return null;
}

// Whole days elapsed. Null when no date.
function daysSinceCount(dateStr?: string | null): number | null {
  if (!dateStr) return null;
  const then = new Date(dateStr).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 86400000));
}

// Compact "days since" label, e.g. "1d". Null when no date.
function daysSince(dateStr?: string | null): string | null {
  const days = daysSinceCount(dateStr);
  return days === null ? null : `${days}d`;
}

// Ready for Delivery is where cases stall quietly: the family has been asked to
// pick a date and simply hasn't answered. The Status column's "3d" only says how
// long the case has been in the stage, never whether anyone was actually asked —
// so name both halves. The un-messaged case matters most: staff who move the
// status by hand can decline to notify (delivery_invite_suppressed_at), and with
// the invite flag off nothing goes out at all, so a case can sit here for a week
// with the family never having heard from us.
function deliveryWaiting(record: any): Suggestion | null {
  // A pickup with no day is staff's to chase, not the family's: no invite ever
  // goes out for a pickup, so "No message sent" would be noise (T-175).
  if (record && pickupNeedsDate(record)) {
    return { tag: 'Delivery', text: PICKUP_TBD_LABEL, variant: 'info' };
  }
  if (record?.cremation_status !== CremationStatus.READY_FOR_DELIVERY) return null;
  if (record?.delivery_confirmed_at) return null;
  // A dated pickup that isn't confirmed is arranged by staff on the phone;
  // there is no message to wait on.
  if (isPickupMethod(record?.delivery_method)) return null;

  const sentAt: string | null = record?.delivery_invite_sent_at ?? null;
  // No invite: fall back to how long the case has been ready, which is what
  // staff need to see when the message is the thing that's missing.
  const days = daysSinceCount(sentAt ?? record?.status_since);
  // Reminders go out on day 3; past that, silence is the exception, not the norm.
  const variant = days !== null && days >= 3 ? 'warning' : 'info';

  if (!sentAt) {
    return {
      tag: 'Delivery',
      text:
        days === null
          ? 'No message sent · waiting for confirmation'
          : `No message sent · ready ${days}d`,
      variant,
    };
  }
  return {
    tag: 'Delivery',
    text: `Message sent ${days}d ago · waiting for confirmation`,
    variant,
  };
}

// Fallback suggestion (when there's no agent work item): the one hint we can
// compute client-side from the contract state.
function suggestionFor(record: any): Suggestion | null {
  if (contractException(record)) {
    return {
      tag: 'Exception',
      text: 'Verify signed contract before pickup',
      variant: 'destructive',
    };
  }
  const waiting = deliveryWaiting(record);
  if (waiting) return waiting;
  // Every paperwork cell is green but the family hasn't been told. Without this
  // the row looks finished yet stays in Delivered, with nothing saying why.
  if (awaitingPaperworkConfirmationEmail(record)) {
    return {
      tag: 'Paperwork',
      text: 'Send paperwork confirmation email',
      variant: 'warning',
    };
  }
  return null;
}

// A real agent suggestion — an open agent_tasks row written by the case_objective
// agent (nestjs WorkItemService). Batch-fetched per group and mapped by case_id.
type WorkItem = {
  id: string;
  case_id: number;
  task_type: string;
  description?: string | null;
  metadata?: {
    kind?: string;
    drafted_action?: { channel?: 'sms' | 'staff'; body?: string; to?: string | null };
    resend_link?: {
      quoteId: string;
      leadId?: string | null;
      email: string;
      clientName: string;
      clientId?: number;
    } | null;
  } | null;
  created_at?: string;
  /** How many OTHER open items this case has behind the one shown (badge). */
  extraCount?: number;
};

const KIND_META: Record<string, { tag: string; variant: Suggestion['variant'] }> = {
  reply: { tag: 'Reply', variant: 'destructive' },
  status: { tag: 'Status', variant: 'info' },
  delivery: { tag: 'Delivery', variant: 'info' },
  paperwork: { tag: 'Paperwork', variant: 'warning' },
  exception: { tag: 'Exception', variant: 'destructive' },
  ingest: { tag: 'Save info', variant: 'warning' },
};

/**
 * The factual "stuck" items (T-186): deterministic, no drafted text. Their
 * own tag and severity, and a click does the real thing — never the SMS
 * composer.
 */
const WATCH_META: Record<string, { tag: string; variant: Suggestion['variant']; focus: string | null }> = {
  delivery_overdue: { tag: 'Delivery overdue', variant: 'destructive', focus: 'delivery' },
  dec_not_ready: { tag: 'DEC', variant: 'warning', focus: 'dec' },
  gov_form_unsigned: { tag: 'OAS form', variant: 'warning', focus: 'oas' },
  paperwork_missing: { tag: 'Paperwork', variant: 'warning', focus: null },
};
const isWatchItem = (w?: WorkItem | null) => !!w && w.task_type in WATCH_META;

// Board shows the top-priority open item per case; the rest surface via a +N
// badge. Ranked by severity, then age (T-186; ingest used to win the slot and
// hide e.g. "DEC not ready (15 d)" behind +1): an overdue delivery first, then
// a family message or exception, then what blocks the next step (DEC, gov
// form, paperwork), then data to save, then the rest. Ties: oldest first.
const TYPE_RANK: Record<string, number> = {
  delivery_overdue: 0,
  dec_not_ready: 2,
  gov_form_unsigned: 2,
  paperwork_missing: 2,
};
const KIND_RANK: Record<string, number> = { reply: 1, exception: 1, ingest: 3 };
function workItemRank(w: WorkItem): number {
  return TYPE_RANK[w.task_type] ?? KIND_RANK[w.metadata?.kind ?? ''] ?? 4;
}
function compareWorkItems(a: WorkItem, b: WorkItem): number {
  const r = workItemRank(a) - workItemRank(b);
  if (r) return r;
  return String(a.created_at ?? '').localeCompare(String(b.created_at ?? ''));
}

// Small severity dot (Linear-style) instead of a loud filled badge.
const DOT_COLOR: Record<Suggestion['variant'], string> = {
  destructive: '#dc2626',
  warning: '#d97706',
  info: '#2563eb',
  success: '#16a34a',
};

// Compact "+N" chip for a case with more than one open suggestion.
function ExtraCountBadge({ n }: { n: number }) {
  return (
    <span
      className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-muted-foreground"
      title={`${n} more open ${n === 1 ? 'suggestion' : 'suggestions'} on this case`}
    >
      +{n}
    </span>
  );
}

function workItemToSuggestion(wi: WorkItem): Suggestion {
  const meta = WATCH_META[wi.task_type] || KIND_META[wi.metadata?.kind || ''] || {
    tag: 'Action',
    variant: 'info' as const,
  };
  return { tag: meta.tag, text: wi.description || 'Suggested action', variant: meta.variant };
}

// Owner is resolved from funerals.owner_id against the profiles map passed in.
function ownerDisplay(
  record: any,
  ownerById: Record<string, { name?: string }>,
): { name: string; initial: string; owned: boolean } {
  const p = record?.owner_id ? ownerById[String(record.owner_id)] : null;
  if (!p) return { name: 'Unassigned', initial: '–', owned: false };
  const name = p.name || '—';
  return { name, initial: name.trim().charAt(0).toUpperCase(), owned: true };
}

// Row tint — mirrors the original board's funeralRowStyle: non-Cleo services
// amber, Ontario cases blue.
function rowTint(record: any): string {
  if (record?.non_paperman_service) return 'bg-[#ffeaba] hover:bg-[#f6dd97]';
  if (record?.region === 'ontario') return 'bg-[#f2f6fd] hover:bg-[#e7eefb]';
  return 'hover:bg-muted/50';
}

const EnrichedFuneralRow = ({
  record,
  ownerById,
  workItemByCaseId,
  isArrangement,
  preneedContract,
}: {
  record: any;
  ownerById: Record<string, { name?: string }>;
  workItemByCaseId: Record<string, WorkItem>;
  isArrangement: boolean;
  preneedContract?: { trustage_status?: string | null; trustage_sent_at?: string | null } | null;
}) => {
  const drawer = useFuneralDrawer();
  const region =
    record?.region === 'ontario' ? 'ON' : record?.region === 'quebec' ? 'QC' : '';
  const exc = contractException(record);
  const stageBadge = isArrangement ? contractStageBadge(record, preneedContract) : null;
  const workItem = workItemByCaseId[String(record.id)];
  // In Arrangement the contract-exception hint isn't a real action (these cases
  // often have no contract yet), so only real agent work items show a suggestion.
  const sug = workItem
    ? workItemToSuggestion(workItem)
    : isArrangement
      ? null
      : suggestionFor(record);
  // Ingest items are approved on the case's feed (the field-write card), not the
  // board composer — so on the board they're a plain label; clicking the row
  // navigates to the case. The +N badge counts the case's other open items.
  const isIngest = workItem?.metadata?.kind === 'ingest';
  const isWatch = isWatchItem(workItem);
  const extraCount = workItem?.extraCount ?? 0;
  const notify = useNotify();
  const navigate = useNavigate();
  // A factual item's click is its action (T-186): resend the paperwork link,
  // or open the case at the right section (OAS opens its panel).
  const runWatchAction = () => {
    if (!workItem) return;
    const link = workItem.metadata?.resend_link;
    if (workItem.task_type === 'paperwork_missing' && link) {
      if (!window.confirm(`Resend the paperwork link to ${link.email}?`)) return;
      resendPaperworkLink(link)
        .then(() => notify(`Paperwork link resent to ${link.email}`, { type: 'success' }))
        .catch((e) => notify(`Failed: ${e instanceof Error ? e.message : String(e)}`, { type: 'error' }));
      return;
    }
    const focus = WATCH_META[workItem.task_type]?.focus;
    navigate(`/funerals/${record.id}/show${focus ? `?focus=${focus}` : ''}`);
  };
  const owner = ownerDisplay(record, ownerById);
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  const [composerOpen, setComposerOpen] = useState(false);

  return (
    <RecordContextProvider value={record}>
      <div
        onClick={() => drawer?.openFuneral(record.id)}
        style={{ gridTemplateColumns: GRID_COLS }}
        className={cn(
          GRID,
          'cursor-pointer border-t border-border px-3 py-2',
          rowTint(record),
        )}
      >
        {/* col 1 — id / region + owner */}
        <div className="flex flex-col gap-1 text-xs text-black">
          <span>{record.id}</span>
          <span className="inline-flex items-center gap-1.5">
            {region && (
              <span className="text-[10px] font-bold tracking-wide text-black">
                {region}
              </span>
            )}
            <span
              title={owner.name}
              className={cn(
                'inline-flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-bold',
                owner.owned
                  ? 'bg-primary text-primary-foreground'
                  : 'border border-border text-black',
              )}
            >
              {owner.initial}
            </span>
          </span>
        </div>

        {/* name — the whole cell falls through to the row → funeral show
            (deceased name no longer links to the client). link={false} keeps the
            ReferenceField as plain text so the click bubbles up. */}
        <div className="min-w-0">
          <div className="text-sm font-bold">
            <ReferenceField source="deceased_client_id" reference="clients" link={false}>
              <TextField source="name" sx={{ color: '#4f3cc9', fontWeight: 700 }} />
            </ReferenceField>
          </div>
          <div className="truncate text-xs text-black">
            <ReferenceField source="primary_client_id" reference="clients" link={false}>
              <TextField source="name" />
            </ReferenceField>
          </div>
          {!isArrangement && exc && (
            <div className="mt-1">
              <Badge variant={exc.variant}>{exc.label}</Badge>
            </div>
          )}
        </div>

        {/* Status column — case age (days in this section) everywhere;
            Arrangement additionally shows Passed + contract stage. */}
        <div className="min-w-0">
          {isArrangement ? (
            (record.date_time_of_death || stageBadge) && (
              <span className="inline-flex flex-col items-start gap-1">
                {stageBadge && (
                  <Badge variant={stageBadge.variant} className="tabular-nums">
                    {stageBadge.label}
                    {daysSince(stageBadge.since) ? ` · ${daysSince(stageBadge.since)}` : ''}
                  </Badge>
                )}
                {record.date_time_of_death && (
                  <Badge
                    variant="outline"
                    className="border-transparent tabular-nums"
                    style={{ backgroundColor: '#475569', color: '#fff' }}
                  >
                    Passed
                    {daysSince(record.date_time_of_death)
                      ? ` · ${daysSince(record.date_time_of_death)}`
                      : ''}
                  </Badge>
                )}
              </span>
            )
          ) : (
            daysSince(record.status_since) && (
              <span
                className="text-xs tabular-nums text-muted-foreground"
                title="Days in this stage"
              >
                {daysSince(record.status_since)}
              </span>
            )
          )}
        </div>

        {/* suggested next action — a real agent work item is clickable
            (opens the composer). */}
        <div className="min-w-0 text-sm">
          {workItem && sug && !isIngest ? (
            // Actionable: calm by default, chevron reveals on hover (Linear-style).
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (isWatch) runWatchAction();
                else setComposerOpen(true);
              }}
              title={isWatch ? workItem.metadata?.drafted_action?.body : undefined}
              className="group -mx-1.5 inline-flex max-w-full cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-muted/70"
            >
              <span
                className="h-1.5 w-1.5 shrink-0 rounded-full"
                style={{ backgroundColor: DOT_COLOR[sug.variant] }}
              />
              <span className="truncate font-medium text-foreground">{sug.text}</span>
              {extraCount > 0 && <ExtraCountBadge n={extraCount} />}
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
            </button>
          ) : sug ? (
            // Non-actionable (incl. ingest): a label — hollow dot, no composer.
            // Clicking the row navigates to the case, where ingest is approved.
            <span className="inline-flex items-center gap-2 text-foreground">
              <span
                className="h-1.5 w-1.5 shrink-0 rounded-full border-[1.5px]"
                style={{ borderColor: DOT_COLOR[sug.variant] }}
              />
              <span className="truncate">{sug.text}</span>
              {extraCount > 0 && <ExtraCountBadge n={extraCount} />}
            </span>
          ) : null}
          {workItem && !isIngest && !isWatch && (
            <WorkItemComposer
              workItem={workItem}
              open={composerOpen}
              onOpenChange={setComposerOpen}
            />
          )}
        </div>

        {/* state cells — reused editors, functionality intact */}
        <div className={STATUS_SUBGRID} style={{ gridTemplateColumns: STATUS_COLS }} onClick={stop}>
          {/* SIED process step — Québec only; Ontario has no SP-3. */}
          <div className="flex justify-center">
            {record.region === 'ontario' ? (
              <span className="text-[11px] text-muted-foreground/60" title="No SIED in Ontario">
                n/a
              </span>
            ) : (
              <SiedStatusCell record={record} />
            )}
          </div>
          <div className="flex justify-center">
            {record.region === 'ontario' ? (
              <span
                className="text-[11px] text-muted-foreground/60"
                title="No registry in Ontario"
              >
                n/a
              </span>
            ) : (
              <CustomBooleanField source="preneed_registry" label="Registry" compact />
            )}
          </div>
          <div className="flex justify-center">
            <DecStatusField source="dec_ready" label="DEC" compact />
          </div>
          <div className="flex justify-center">
            <BenefitsListCell />
          </div>
          <div className="flex justify-center">
            <CustomBooleanField source="attestation_ready" label="Attestation" compact />
          </div>
        </div>
      </div>
    </RecordContextProvider>
  );
};

export const EnrichedFuneralGroup = ({
  status,
  label,
  color,
  defaultExpanded = true,
  baseFilters,
  sort,
  rowFilter,
}: {
  status: CremationStatus;
  label: string;
  color: string;
  defaultExpanded?: boolean;
  baseFilters: Record<string, any>;
  sort: { field: string; order: string };
  /** Client-side split of a shared status fetch (e.g. Delivered vs Closed). */
  rowFilter?: (r: any) => boolean;
}) => {
  const isArrangement = status === CremationStatus.ARRANGEMENT;

  // Every group honors its defaultExpanded prop on load (Arrangement and
  // Delivered open, Closed collapsed); the header toggles from there.
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Every group folds older cases behind a "Show N older" toggle.
  const [showAll, setShowAll] = useState(false);

  // Toolbar toggles (FuneralListToolBar): Owner (all|mine) and Show (all|need).
  const [ownerFilter] = useStore<string>('funerals.owner', 'all');
  const [viewFilter] = useStore<string>('funerals.view', 'all');
  const { identity } = useGetIdentity();

  // Fetch the WHOLE group, not a page — a 50-row cap silently dropped older
  // delivered cases from both Delivered and Closed (their split is client-side
  // on this shared fetch). Long groups stay light via the "Show N older" fold.
  // 1000 is Supabase's max-rows per request; Delivered stays bounded because
  // cases move to Closed once paperwork is green.
  const { data, isLoading } = useGetList('funerals', {
    filter: { ...baseFilters, cremation_status: status },
    sort: { field: sort?.field || 'id', order: (sort?.order || 'DESC') as 'ASC' | 'DESC' },
    pagination: { page: 1, perPage: 1000 },
  });

  // Pre-need TruStage status (T-162) for the pre-need rows, one query per group.
  const preneedIds = useMemo(
    () => (isArrangement ? (data || []).filter((r: any) => r.has_preneed && !r.has_atneed).map((r: any) => r.id) : []),
    [data, isArrangement],
  );
  // `contracts` directly: the react-admin resource reads the contracts_list
  // view, which doesn't carry the TruStage columns.
  const { data: preneedContracts } = useQuery(
    ['preneed_contracts_status', preneedIds.join(',')],
    async () => {
      const { data: rows, error } = await supabase
        .from('contracts')
        .select('id, funeral_id, trustage_status, trustage_sent_at')
        .in('funeral_id', preneedIds)
        .eq('is_preneed', true)
        .order('id', { ascending: false });
      if (error) throw error;
      return (rows as any[]) ?? [];
    },
    { enabled: preneedIds.length > 0, staleTime: 60 * 1000 },
  );
  const preneedContractByFuneral = useMemo(() => {
    const map: Record<string, any> = {};
    for (const c of preneedContracts || []) if (!map[String(c.funeral_id)]) map[String(c.funeral_id)] = c;
    return map;
  }, [preneedContracts]);

  // Staff lookup so rows can show the owner's avatar/name (funerals.owner_id → profiles).
  const { data: profiles } = useGetList('profiles', {
    pagination: { page: 1, perPage: 200 },
    sort: { field: 'name', order: 'ASC' },
  });
  const ownerById = useMemo(() => {
    const m: Record<string, { name?: string }> = {};
    (profiles || []).forEach((p: any) => {
      m[String(p.id)] = p;
    });
    return m;
  }, [profiles]);

  // Open agent work items (the "Suggested next action") — batch-fetched and
  // mapped by case_id, mirroring the owner lookup above.
  const { data: workItems } = useGetList('agent_tasks', {
    filter: { status: 'pending' },
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'created_at', order: 'DESC' },
  });
  const workItemByCaseId = useMemo(() => {
    const groups: Record<string, WorkItem[]> = {};
    (workItems || []).forEach((w: any) => {
      (groups[String(w.case_id)] ??= []).push(w as WorkItem);
    });
    const m: Record<string, WorkItem> = {};
    for (const [caseId, items] of Object.entries(groups)) {
      // Stable sort by priority; the fetch already ordered created_at DESC, so
      // same-rank ties keep newest-first. Top item shows; the rest → +N badge.
      items.sort(compareWorkItems);
      m[caseId] = { ...items[0], extraCount: items.length - 1 };
    }
    return m;
  }, [workItems]);

  const rows = useMemo(
    () =>
      (data || []).filter((r) => {
        const okOwner =
          ownerFilter !== 'mine' ||
          (identity != null && String(r.owner_id) === String(identity.id));
        const okView =
          viewFilter !== 'need' ||
          workItemByCaseId[String(r.id)] != null ||
          (!isArrangement && suggestionFor(r) != null);
        const okRow = !rowFilter || rowFilter(r);
        return okOwner && okView && okRow;
      }),
    [data, ownerFilter, viewFilter, identity, workItemByCaseId, rowFilter, isArrangement],
  );

  // Fold: every group shows its most recent rows and tucks the rest behind
  // "Show N older". Arrangement additionally surfaces any case that has a
  // sent/signed contract or has passed (even if older); expanding shows the
  // full list in sort order, so the surfaced ones settle into their natural
  // place. olderCount stays computed while expanded so "Show less" can render.
  const { foldedRows, olderCount } = useMemo(() => {
    if (!isArrangement) {
      if (rows.length <= FOLD_COUNT) return { foldedRows: rows, olderCount: 0 };
      return {
        foldedRows: rows.slice(0, FOLD_COUNT),
        olderCount: rows.length - FOLD_COUNT,
      };
    }
    const surfaced: any[] = [];
    let hidden = 0;
    rows.forEach((r, i) => {
      const cs: ContractSummary[] = r.contracts_summary || [];
      const sentOrSigned = cs.some(
        (c) => c.status === 'sent' || c.status === 'signed' || c.status === 'paid',
      );
      const passed = !!r.date_time_of_death;
      // Surface the ARRANGEMENT_FOLD_COUNT most recent, plus any case that has
      // passed or whose contract has been sent/signed (and is still sitting in
      // Arrangement). Routine created-only contracts fold with the rest.
      if (i < ARRANGEMENT_FOLD_COUNT || sentOrSigned || passed) surfaced.push(r);
      else hidden += 1;
    });
    return { foldedRows: surfaced, olderCount: hidden };
  }, [rows, isArrangement]);
  const displayRows = showAll ? rows : foldedRows;

  // Hide the group when nothing (unfiltered) or nothing matches the filters.
  if (!isLoading && rows.length === 0) return null;

  return (
    <div className="cleo-ui overflow-hidden rounded-lg border border-border bg-card text-card-foreground">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{ backgroundColor: color }}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        <span className="text-[15px] font-bold text-foreground">{label}</span>
        <Badge variant="secondary" className="font-bold">
          {isLoading ? '…' : rows.length}
        </Badge>
        <ChevronRight
          className={cn(
            'ml-auto h-4 w-4 text-black transition-transform',
            expanded && 'rotate-90',
          )}
        />
      </button>

      {expanded && (
        <div>
          <div
            style={{ gridTemplateColumns: GRID_COLS }}
            className={cn(
              GRID,
              'border-b border-border px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-black',
            )}
          >
            <span>Id</span>
            <span>Name</span>
            <span>Status</span>
            <span>Suggested next action</span>
            <div
              className={cn(STATUS_SUBGRID, 'text-center')}
              style={{ gridTemplateColumns: STATUS_COLS }}
            >
              <span title="SP-3 step in Québec's SIED portal">SIED</span>
              <span>Reg</span>
              <span>DEC</span>
              <span>Ben</span>
              <span>Att</span>
            </div>
          </div>
          <BenefitsStatusProvider funeralIds={displayRows.map((r) => r.id)}>
            {displayRows.map((record) => (
              <EnrichedFuneralRow
                key={record.id}
                record={record}
                ownerById={ownerById}
                workItemByCaseId={workItemByCaseId}
                isArrangement={isArrangement}
                preneedContract={preneedContractByFuneral[String(record.id)]}
              />
            ))}
          </BenefitsStatusProvider>
          {olderCount > 0 && (
            <div className="border-t border-border py-1.5 text-center">
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                className="text-xs font-semibold text-primary hover:underline"
              >
                {showAll ? 'Show less' : `Show ${olderCount} older`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
